/**
 * OverflowTabs Component
 * 
 * A smart tab navigation component that automatically handles tab overflow by moving
 * excess tabs into a "More" dropdown when they don't fit within the container width.
 * 
 * Features:
 * - Automatic overflow detection based on container width
 * - Responsive design that adapts to window resize
 * - Minimum visible tabs guarantee (at least 3 when possible)
 * - Customizable styling through className props
 * - Support for disabled tabs
 * - Smooth transitions and hover states
 * - Accessible keyboard navigation
 * 
 * @example
 * ```tsx
 * const tabs = [
 *   { key: "overview", label: "Overview", icon: <HomeIcon /> },
 *   { key: "settings", label: "Settings", icon: <SettingsIcon /> },
 *   // ... more tabs
 * ];
 * 
 * <OverflowTabs
 *   tabs={tabs}
 *   activeTab="overview"
 *   onTabChange={(tabKey) => setActiveTab(tabKey)}
 * />
 * ```
 */

import React, { useEffect, useState, useRef } from "react";
import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { MoreHorizontalIcon } from "lucide-react";

export interface TabItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface OverflowTabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tabKey: string) => void;
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
  inactiveTabClassName?: string;
  moreButtonClassName?: string;
  dropdownClassName?: string;
}

export const OverflowTabs: React.FC<OverflowTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className,
  tabClassName,
  activeTabClassName,
  inactiveTabClassName,
  moreButtonClassName,
  dropdownClassName,
}) => {
  // Tab overflow detection
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [visibleTabs, setVisibleTabs] = useState<TabItem[]>([]);
  const [overflowTabs, setOverflowTabs] = useState<TabItem[]>([]);
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  // Tab overflow detection logic
  useEffect(() => {
    const detectTabOverflow = () => {
      if (!tabContainerRef.current) return;
      
      const container = tabContainerRef.current;
      const containerWidth = container.offsetWidth;
      
      // Approximate width calculation for tabs
      // Each tab has: icon (16px) + gap (8px) + text + padding (32px) + margin (16px)
      const tabWidths = tabs.map(tab => {
        const textLength = tab.label.length;
        return 16 + 8 + (textLength * 8) + 32 + 16; // Rough estimation
      });
      
      // Reserve space for the "More" button (approximately 80px)
      const moreButtonWidth = 80;
      const availableWidth = containerWidth - moreButtonWidth;
      
      let totalWidth = 0;
      let visibleCount = 0;
      
      for (let i = 0; i < tabWidths.length; i++) {
        if (totalWidth + tabWidths[i] <= availableWidth) {
          totalWidth += tabWidths[i];
          visibleCount++;
        } else {
          break;
        }
      }
      
      // Always show at least 3 tabs if possible
      const minVisibleTabs = Math.min(3, tabs.length);
      visibleCount = Math.max(visibleCount, minVisibleTabs);
      
      // If all tabs fit, don't show overflow
      if (visibleCount >= tabs.length) {
        setVisibleTabs(tabs);
        setOverflowTabs([]);
      } else {
        setVisibleTabs(tabs.slice(0, visibleCount));
        setOverflowTabs(tabs.slice(visibleCount));
      }
    };

    // Initial detection
    detectTabOverflow();
    
    // Re-detect on window resize
    const handleResize = () => {
      setTimeout(detectTabOverflow, 100); // Debounce
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [tabs]);

  const handleTabClick = (tabKey: string, disabled?: boolean) => {
    if (disabled) return;
    onTabChange(tabKey);
  };

  const getTabButtonClasses = (tab: TabItem, isActive: boolean) => {
    const baseClasses = "flex items-center gap-2 px-4 py-2 text-sm font-gc-med rounded-md transition-colors whitespace-nowrap";
    const defaultActiveClasses = "bg-maitai-green/10 text-maitai-green";
    const defaultInactiveClasses = "text-muted-foreground hover:text-foreground hover:bg-accent/50";
    const disabledClasses = "opacity-50 cursor-not-allowed";
    
    return cn(
      baseClasses,
      tabClassName,
      isActive 
        ? (activeTabClassName || defaultActiveClasses)
        : (inactiveTabClassName || defaultInactiveClasses),
      tab.disabled && disabledClasses
    );
  };

  const getMoreButtonClasses = () => {
    const baseClasses = "flex items-center gap-2 px-4 py-2 text-sm font-gc-med rounded-md transition-colors whitespace-nowrap";
    const hasActiveOverflow = overflowTabs.some(tab => tab.key === activeTab);
    const defaultActiveClasses = "bg-maitai-green/10 text-maitai-green";
    const defaultInactiveClasses = "text-muted-foreground hover:text-foreground hover:bg-accent/50";
    
    return cn(
      baseClasses,
      moreButtonClassName,
      hasActiveOverflow ? defaultActiveClasses : defaultInactiveClasses
    );
  };

  return (
    <div ref={tabContainerRef} className={cn("px-8 py-4", className)}>
      <nav className="flex items-center space-x-4">
        {/* Visible tabs */}
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabClick(tab.key, tab.disabled)}
            disabled={tab.disabled}
            className={getTabButtonClasses(tab, activeTab === tab.key)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
        
        {/* Overflow tabs dropdown */}
        {overflowTabs.length > 0 && (
          <Popover open={isMoreOpen} onOpenChange={setIsMoreOpen}>
            <PopoverTrigger asChild>
              <button className={getMoreButtonClasses()}>
                <MoreHorizontalIcon className="w-4 h-4" />
                More
              </button>
            </PopoverTrigger>
            <PopoverContent className={cn("w-48 p-2", dropdownClassName)} align="start">
              <div className="space-y-1">
                {overflowTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => {
                      handleTabClick(tab.key, tab.disabled);
                      setIsMoreOpen(false);
                    }}
                    disabled={tab.disabled}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors text-left",
                      activeTab === tab.key
                        ? "bg-maitai-green/10 text-maitai-green"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
                      tab.disabled && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </nav>
    </div>
  );
};

export default OverflowTabs; 